import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { TranslateService } from '@ngx-translate/core';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { GeographieSelectionPopinComponent, GeographieSelectionPopinData } from '@share/component/geographie-selection-popin/geographie-selection-popin.component';
import { TypeGeographie } from '@domain/geographie/typeGeographie';
import { PorteeGeographie } from '@share/component/autocomplete/options/geographie';
import { GeographieView } from '@domain/geographie/geographieView';
import { FraisMissionRegion } from '@domain/od/engagements/indemnites/frais-mission-region';

/**
 * Configuration par défaut pour la sélection de territoire
 */
export const TERRITOIRE_CONFIG = {
    listeTypes: [TypeGeographie.PAYS, TypeGeographie.VILLE, TypeGeographie.ZONE, TypeGeographie.REGION],
    contexte: PorteeGeographie.INDEMNITE
};

/**
 * Utilitaires communs pour les formulaires de frais de mission
 */
export class FraisMissionFormUtils {

    /**
     * Ouvre la popin de sélection de territoire
     * @param dialog Service MatDialog
     * @param translateService Service de traduction
     * @param destroy$ Subject de destruction pour unsubscribe
     * @param onSelected Callback appelé avec la géographie sélectionnée
     */
    static openTerritoirePopin(
        dialog: MatDialog,
        translateService: TranslateService,
        destroy$: Subject<void>,
        onSelected: (geo: GeographieView) => void
    ): void {
        const dialogRef = dialog.open(GeographieSelectionPopinComponent, {
            minWidth: 600,
            data: {
                listeTypes: TERRITOIRE_CONFIG.listeTypes,
                contexte: TERRITOIRE_CONFIG.contexte,
                title: translateService.instant('admin.frais.indemnites.fraisMissions.detail.territoirePopin.title')
            } as GeographieSelectionPopinData
        });

        dialogRef.afterClosed().pipe(
            filter(result => !!result),
            takeUntil(destroy$)
        ).subscribe((geo: GeographieView) => {
            onSelected(geo);
        });
    }

    /**
     * Vérifie la validité de la période de validité
     * @param periodeMin Période min
     * @param periodeMax Période max (optionnel)
     * @returns true si la période est valide
     */
    static isPeriodeValide(periodeMin: number, periodeMax: number): boolean {
        //Vérifie que la période min est définie et supérieure ou égale à 1
        if (periodeMin == null || periodeMin < 1) {
            return false;
        }
        //Si la période max est définie, elle doit être supérieure ou égale à la période min
        if (periodeMax != null && periodeMax < periodeMin) {
            return false;
        }
        return true;
    }

    /**
     * Gère le changement de la case taux spécial
     * Retourne le libellé à utiliser (null si taux spécial décoché)
     * @param hasTauxSpecial Valeur de la case
     * @param libelleSpecial Libellé actuel
     * @returns Libellé à conserver ou null
     */
    static getLibelleSpecialOnChange(hasTauxSpecial: boolean, libelleSpecial: string): string | null {
        return hasTauxSpecial ? libelleSpecial : null;
    }

    /**
     * Mappe un GeographieView vers FraisMissionRegion
     */
    static mapGeographieToRegion(geo: GeographieView): FraisMissionRegion {
        const region: FraisMissionRegion = { idTaux: 0 };
        region.libelleTerritoire = geo.libelle;

        switch (geo.type) {
            case TypeGeographie.PAYS:
                region.idPays = geo.id;
                break;
            case TypeGeographie.VILLE:
                region.idVille = geo.id;
                break;
            case TypeGeographie.ZONE:
                region.idZone = geo.id;
                break;
            case TypeGeographie.REGION:
                region.idRegion = geo.id;
                break;
        }
        return region;
    }

    /**
     * Mappe un FraisMissionRegion vers GeographieView
     */
    static mapRegionToGeographie(region: FraisMissionRegion): GeographieView | null {
        if (!region) {
            return null;
        }

        const geo: GeographieView = {
            id: null,
            type: null,
            libelle: region.libelleTerritoire
        };

        if (region.idPays) {
            geo.id = region.idPays;
            geo.type = TypeGeographie.PAYS;
        } else if (region.idVille) {
            geo.id = region.idVille;
            geo.type = TypeGeographie.VILLE;
        } else if (region.idZone) {
            geo.id = region.idZone;
            geo.type = TypeGeographie.ZONE;
        } else if (region.idRegion) {
            geo.id = region.idRegion;
            geo.type = TypeGeographie.REGION;
        }

        return geo;
    }
}